import { useNavigate } from "react-router-dom";

import { CountPill } from "~/components/kiosk/products/quantity-controls/count-pill";
import { Stepper } from "~/components/kiosk/products/quantity-controls/stepper";
import { kioskActions } from "~/stores/kiosk-session/actions";
import {
  useKioskProductLastLineId,
  useKioskProductQuantity
} from "~/stores/kiosk-session/hooks/use-kiosk-product-basket";
import { getIsAddDisabled } from "~/stores/kiosk-session/hooks/use-kiosk-products/get-is-add-disabled";
import { type KioskDisplayProductList } from "~/stores/kiosk-session/hooks/use-kiosk-products/types";

import { getProductDetailPath } from "./get-product-detail-path";

type ProductQuantityControlProps = {
  item: KioskDisplayProductList;
  discountId?: string;
};

export const ProductQuantityControl = ({
  item,
  discountId
}: ProductQuantityControlProps) => {
  const navigate = useNavigate();
  const { product } = item;
  const quantity = useKioskProductQuantity(product.id);
  const lastLineId = useKioskProductLastLineId(product.id);

  const isIncrementDisabled = getIsAddDisabled(item);

  // Keep the control visible whenever the basket already has this product so the
  // shopper can still remove items - even after hitting a stock/user limit that
  // blocks adding. Only hide it when there is nothing to add or remove.
  if (quantity === 0 && isIncrementDisabled) {
    return null;
  }

  const openDetail = () => {
    navigate(getProductDetailPath(product.id, discountId));
  };

  // Only simple, non-redeemable products add directly; everything else opens
  // the modal (each confirmation creates its own basket line).
  const isInline = product.type === "regular" && !discountId;
  if (!isInline) {
    // With nothing in the basket the pill just opens the modal to add. Once
    // there are items, switch to a stepper so they can be removed directly.
    if (quantity === 0) {
      return (
        <CountPill
          value={quantity}
          onClick={openDetail}
          className="pointer-events-auto"
        />
      );
    }

    return (
      <Stepper
        className="pointer-events-auto"
        value={quantity}
        isIncrementDisabled={isIncrementDisabled}
        onIncrement={openDetail}
        onDecrement={() => {
          if (lastLineId) {
            kioskActions.basket.decrement(lastLineId);
          }
        }}
      />
    );
  }

  return (
    <Stepper
      className="pointer-events-auto"
      value={quantity}
      isIncrementDisabled={isIncrementDisabled}
      onIncrement={() => {
        kioskActions.basket.add(item, { quantity: 1 });
      }}
      onDecrement={() => {
        if (lastLineId) {
          kioskActions.basket.decrement(lastLineId);
        }
      }}
    />
  );
};
